import type { ShapeItem } from '../../doc/schema';

/** Rect, ellipse, line, arrow drawn as SVG sized to the item box (transient size included). */
export function ShapeBody({ item, w, h }: { item: ShapeItem; w: number; h: number }) {
  const { kind, fill, stroke, strokeWidth: sw, flip } = item.props;
  const common = { fill: fill === 'none' ? 'none' : fill, stroke, strokeWidth: sw };

  let body;
  switch (kind) {
    case 'rect':
      body = <rect x={sw / 2} y={sw / 2} width={Math.max(0, w - sw)} height={Math.max(0, h - sw)} {...common} />;
      break;
    case 'ellipse':
      body = (
        <ellipse cx={w / 2} cy={h / 2} rx={Math.max(0, (w - sw) / 2)} ry={Math.max(0, (h - sw) / 2)} {...common} />
      );
      break;
    case 'line':
    case 'arrow': {
      const x1 = 0;
      const y1 = flip ? h : 0;
      const x2 = w;
      const y2 = flip ? 0 : h;
      const head = kind === 'arrow' ? arrowHead(x1, y1, x2, y2, Math.max(10, sw * 4)) : null;
      body = (
        <>
          <line x1={x1} y1={y1} x2={head ? head.base.x : x2} y2={head ? head.base.y : y2} stroke={stroke} strokeWidth={sw} strokeLinecap="square" />
          {head && <polygon points={head.points} fill={stroke} />}
        </>
      );
      break;
    }
  }

  return (
    <svg className="shape-svg" width={w} height={h} viewBox={`0 0 ${w} ${h}`} overflow="visible" aria-hidden>
      {body}
    </svg>
  );
}

function arrowHead(x1: number, y1: number, x2: number, y2: number, size: number) {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const len = Math.hypot(x2 - x1, y2 - y1);
  const s = Math.min(size, len);
  const bx = x2 - Math.cos(angle) * s;
  const by = y2 - Math.sin(angle) * s;
  const half = s * 0.45;
  const lx = bx + Math.cos(angle + Math.PI / 2) * half;
  const ly = by + Math.sin(angle + Math.PI / 2) * half;
  const rx = bx + Math.cos(angle - Math.PI / 2) * half;
  const ry = by + Math.sin(angle - Math.PI / 2) * half;
  return { base: { x: bx, y: by }, points: `${x2},${y2} ${lx},${ly} ${rx},${ry}` };
}
